import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Phone, Mail, MapPin } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="terms-of-service-page">
      <SEOHead 
        title="Terms of Service | Amy C. Brown Law"
        description="Terms of service for Amy C. Brown Law website. Read our terms and conditions for using this website."
      />
      {/* Header */}
      <section className="py-12 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 text-primary mb-4">
            <FileText className="w-6 h-6" />
            <span className="text-sm font-medium uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-300">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Agreement to Terms</h2>
            <p className="text-slate-700 mb-6">
              By accessing and using the Amy C. Brown Law website (amycbrownlaw.com), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">No Attorney-Client Relationship</h2>
            <p className="text-slate-700 mb-6">
              <strong>Important:</strong> Using this website, submitting a contact form, or communicating through this website does not create an attorney-client relationship. An attorney-client relationship is only formed when Amy C. Brown Law expressly agrees in writing to represent you. Until such agreement is made, any information you provide is not protected by attorney-client privilege.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Informational Purposes Only</h2>
            <p className="text-slate-700 mb-6">
              The content on this website is provided for general informational purposes only and does not constitute legal advice. Every legal situation is unique, and the information provided may not apply to your specific circumstances. You should not act or refrain from acting based on information on this website without seeking professional legal counsel.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">No Guarantees</h2>
            <p className="text-slate-700 mb-6">
              Past results do not guarantee future outcomes. Any case results, testimonials, or examples mentioned on this website are not a guarantee that similar results will be achieved in your case. Each case is different and must be evaluated on its own merits.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Geographic Limitations</h2>
            <p className="text-slate-700 mb-6">
              Amy C. Brown is licensed to practice law in Washington State. Information on this website is intended for residents of Washington State and Alaska (for maritime matters). If you are located outside these areas, please consult with an attorney licensed in your jurisdiction.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Compensation Estimator Disclaimer</h2>
            <p className="text-slate-700 mb-6">
              The Case Value Calculator (Compensation Estimator) on this website provides general estimates for educational purposes only. Actual case values vary significantly based on specific facts, evidence, liability, insurance coverage, and many other factors. The calculator is not a substitute for a professional case evaluation.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Intellectual Property</h2>
            <p className="text-slate-700 mb-6">
              All content on this website, including text, graphics, logos, images, and software, is the property of Amy C. Brown Law and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or use any content without prior written permission.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Third-Party Links</h2>
            <p className="text-slate-700 mb-6">
              This website may contain links to third-party websites. These links are provided for convenience only. Amy C. Brown Law does not endorse or control the content of third-party sites and is not responsible for their content or privacy practices.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Limitation of Liability</h2>
            <p className="text-slate-700 mb-6">
              Amy C. Brown Law shall not be liable for any damages arising from your use of this website or reliance on any information provided herein. This includes, but is not limited to, direct, indirect, incidental, consequential, or punitive damages.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Changes to Terms</h2>
            <p className="text-slate-700 mb-6">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this website. Your continued use of the website constitutes acceptance of the modified terms.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Governing Law</h2>
            <p className="text-slate-700 mb-6">
              These Terms of Service are governed by the laws of the State of Washington, without regard to conflict of law principles.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-8 mb-4">Contact Us</h2>
            <p className="text-slate-700 mb-4">If you have questions about these Terms of Service, please contact us:</p>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <p className="font-bold text-secondary mb-3">Amy C. Brown Law</p>
              <div className="space-y-2 text-slate-700">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  <a href="tel:425-675-1224" className="hover:text-primary">(425) 675-1224</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <a href="mailto:amy@amycbrownlaw.com" className="hover:text-primary">amy@amycbrownlaw.com</a>
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Everett, Washington
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
